import { Button } from "primereact/button";
import { Chips, type ChipsChangeEvent } from "primereact/chips";
import { InputText } from "primereact/inputtext";
import type { Nullable } from "primereact/ts-helpers";
import { useEffect, useState } from "react";
import type {
  APIColumnDataSourceConfig,
  APIMethodType,
} from "../../../../../../../../api/infrastructure/table.formatter";
import { showToast } from "../../../../../../../shared/global-tools/toast-system";
import { validateHeaders } from "../../../../../../../shared/validate-rest-api-headers";
import { validateUrl } from "../../../../../../../shared/validate-url";
import OurDropdown from "../../../../../../utils/OurDropdown/OurDropdown";
import "./TableColumnAPIForm.scss";

const API_METHODS = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
];

const AUTH_TYPES = [
  { label: "Aucune", value: "none" },
  { label: "Bearer", value: "bearer" },
];

interface TableColumnAPIFormProps {
  apiConfig?: APIColumnDataSourceConfig;
  close: () => void;
  onChange(config: APIColumnDataSourceConfig): void;
  onCancel?(): void;
}

export default function TableColumnAPIForm({
  apiConfig,
  close,
  onChange,
  onCancel,
}: TableColumnAPIFormProps) {
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState<APIMethodType>("GET");
  const [authType, setAuthType] = useState<"none" | "bearer"| "apiKey">("none");
  const [authToken, setAuthToken] = useState("");
  const [headers, setHeaders] = useState<Nullable<string[]>>(null);
  const [queryParams, setQueryParams] = useState<Nullable<string[]>>(null);
  const [targetFields, setTargetFields] = useState<Nullable<string[]>>(null);

  const handleSubmit = () => {
    if (!validateUrl(url)) {
      showToast("L'URL saisie n'est pas valide", {
        severity: "warn",
      });
      return;
    }
    if (!targetFields) {
      showToast("Les champs cibles sont requis", {
        severity: "warn",
      });
      return;
    }
    if (authType === "bearer" && authToken.trim() === "") {
      showToast("Le token d'authentification est requis", {
        severity: "warn",
      });
      return;
    }
    onChange({
      url,
      method,
      headers: headers ?? undefined,
      queryParams: queryParams ?? undefined,
      targetFields,
      authType,
      authToken: authType === "bearer" ? authToken : undefined,
    });
    close();
  };

  useEffect(() => {
    if (apiConfig) {
      setAuthType(apiConfig.authType ?? "none");
      setAuthToken(apiConfig.authToken ?? "");
      setUrl(apiConfig.url);
      setMethod(apiConfig.method);
      setHeaders(apiConfig.headers ?? null);
      setQueryParams(apiConfig.queryParams ?? null);
      setTargetFields(apiConfig.targetFields ?? null);
    }
  }, [apiConfig]);

  return (
    <div className="TableColumnAPIForm">
      <div className="TableColumnAPIForm-header">
        <h2 className="TableColumnAPIForm-headerTitle">Configuration API</h2>
      </div>

      <div className="TableColumnAPIForm-body">
        <form
          className="TableColumnAPIForm-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              URL
            </span>
            <InputText
              className="Field-input small TableColumnAPIForm-input w-full"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
              }}
              required
            />
          </label>
          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              METHODE
            </span>

            <OurDropdown<APIMethodType>
              initialOptions={API_METHODS}
              className="sm TableColumnAPIForm-dropdown w-full"
              selectedOption={method}
              onChange={setMethod}
            />
          </label>

          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Type d'authentification
            </span>

            <OurDropdown<"none" | "bearer"| "apiKey">
              initialOptions={AUTH_TYPES}
              className="sm TableColumnAPIForm-dropdown w-full"
              selectedOption={authType}
              onChange={setAuthType}
            />
          </label>

          {authType === "bearer" && (
            <label className="Field">
              {/* TODO: standardize styles */}
              <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
                Token
              </span>
              <InputText
                className="Field-input small TableColumnAPIForm-input w-full"
                value={authToken}
                onChange={(e) => {
                  setAuthToken(e.target.value);
                }}
                required={authType === "bearer"}
              />
            </label>
          )}

          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              HEADERS
            </span>

            <Chips
              className="TableColumnAPIForm-chips"
              value={headers ?? []}
              onChange={(e: ChipsChangeEvent) => {
                if (!validateHeaders(e.value)) {
                  showToast(`L'entête '${e.value}' est invalide`, {
                    severity: "warn",
                  });
                  return;
                }
                setHeaders(e.value);
              }}
              placeholder="Content-Type=application/json"
            />
          </label>
          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              QUERY
            </span>
            <Chips
              className="TableColumnAPIForm-chips"
              value={queryParams ?? []}
              onChange={(e: ChipsChangeEvent) => {
                if (!validateQueryParams(e.value)) {
                  showToast(`Le paramètre '${e.value}' est invalide`, {
                    severity: "warn",
                  });
                  return;
                }
                setQueryParams(e.value?.map((param) => param.toLowerCase()));
              }}
              placeholder="limit=50"
            />
          </label>

          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Champs cibles
            </span>
            <Chips
              className="TableColumnAPIForm-chips"
              value={targetFields ?? []}
              onChange={(e: ChipsChangeEvent) => {
                if (!validateTargetFields(e.value)) {
                  showToast(`Le champ cible '${e.value}' est invalide`, {
                    severity: "warn",
                  });
                  return;
                }
                setTargetFields(e.value?.map((param) => param));
              }}
              placeholder="firstname"
            />
          </label>

          <div className="TableColumnAPIForm-formActions">
            <Button
              type="button"
              label="Annuler"
              className="TableColumnAPIForm-btn Btn secondary outlined"
              onClick={() => {
                onCancel?.();
                close();
              }}
            />
            <Button
              type="submit"
              label="Enregistrer"
              className="TableColumnAPIForm-btn Btn primary"
            />
          </div>
        </form>
      </div>
    </div>
  );
}

function validateQueryParams(queryParams: Nullable<string[]>): boolean {
  if (!queryParams) return false;
  return queryParams.every((param) => {
    const val = param.trim().replace(/\s+/g, "");
    if (val === "") return false;
    const match = val.match(/^([^=:]+)=([^=:]+)$/);
    if (!match) return false;
    const [, key, value] = match.map((s) => s.trim());
    if (key === "" || value === "") return false;
    return true;
  });
}

function validateTargetFields(targetFields: Nullable<string[]>): boolean {
  if (!targetFields) return false;
  return targetFields.every((param) => {
    const val = param.trim().replace(/\s+/g, "");
    if (val === "") return false;
    return true;
  });
}
